/**
 * ChatInlineElement component for the Indigo design system.
 *
 * A small inline pill used inside chat bubbles to surface a structured
 * reference such as a physician, contact, appointment, time, location,
 * chart, or any custom entity.
 *
 * Predefined types render with a baked-in 12×12 icon and a fixed style.
 * The `custom` type lets the caller supply their own icon node.
 *
 * Optional `cardPreview` content is rendered as a Tippy-driven popup on
 * hover/focus (typically a `ChatCardPreview`). When no preview is provided
 * the element simply behaves as a static pill (or a button if `onClick`
 * is supplied).
 *
 * @example
 * ```tsx
 * <ChatInlineElement type="physician" label="Dr. Annette Zhu" />
 *
 * <ChatInlineElement
 *   type="location"
 *   label="Santa Clara Clinic"
 *   onClick={() => openLocation(id)}
 *   cardPreview={
 *     <ChatCardPreview type="location" title="..." subtitle="..." />
 *   }
 * />
 * ```
 */

import Tippy from "@tippyjs/react";
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Icon, type IconProps } from "../../Icons";
import { Body } from "../../Typography/Body";
import "./ChatInlineElement.css";

export type ChatInlineElementType =
  | "physician"
  | "contact"
  | "appointment"
  | "time"
  | "location"
  | "chart"
  | "custom";

export interface ChatInlineElementProps {
  /** Predefined inline-element kind. Use `"custom"` to supply your own icon. */
  type: ChatInlineElementType;
  /** Visible label. */
  label: string;
  /** Custom icon node. Required when `type === "custom"`. */
  icon?: ReactNode;
  /** Click handler. Renders the element as a button when provided. */
  onClick?: () => void;
  /**
   * Optional preview node rendered on hover/focus as a popup tooltip.
   * Typically a `ChatCardPreview`. Currently used for `physician` and
   * `location` types in chat surfaces.
   */
  cardPreview?: ReactNode;
  /** Additional CSS class applied to the root element. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

const PREDEFINED_ICON_NAME: Record<
  Exclude<ChatInlineElementType, "custom">,
  IconProps["name"]
> = {
  physician: "physician-icon",
  contact: "contact-book",
  appointment: "calendar-appointment",
  time: "clock",
  location: "location-icon",
  chart: "browser-window",
};

export const ChatInlineElement = forwardRef<
  HTMLElement,
  ChatInlineElementProps
>(({ type, label, icon, onClick, cardPreview, className, ...rest }, ref) => {
  const dataTestId = rest["data-testid"];
  const isCustom = type === "custom";

  const iconNode = isCustom ? (
    icon
  ) : (
    <Icon name={PREDEFINED_ICON_NAME[type]} size="xs" />
  );

  const isInteractive = Boolean(onClick);
  const Component = isInteractive ? "button" : "span";

  const rootClass = clsx(
    "indigo-chat-inline-element",
    isInteractive && "indigo-chat-inline-element--button",
    className,
  );

  const elementProps = isInteractive
    ? {
        type: "button" as const,
        onClick,
      }
    : {};

  const element = (
    <Component
      ref={ref as React.Ref<HTMLButtonElement & HTMLSpanElement>}
      className={rootClass}
      data-testid={dataTestId}
      {...elementProps}
    >
      <span className="indigo-chat-inline-element__icon">{iconNode}</span>
      <Body as="span" size="sm" className="indigo-chat-inline-element__label">
        {label}
      </Body>
    </Component>
  );

  if (cardPreview) {
    return (
      <Tippy
        content={cardPreview}
        placement="bottom-start"
        interactive
        delay={[150, 0]}
        duration={[200, 0]}
        offset={[0, 6]}
        appendTo={() => document.body}
        // The popup itself supplies its own background/shadow, so render
        // Tippy's wrapper transparent and unpadded.
        theme="indigo-chat-inline-preview"
      >
        {element}
      </Tippy>
    );
  }

  return element;
});

ChatInlineElement.displayName = "ChatInlineElement";

export default ChatInlineElement;
